import React from 'react';

const formatSavedTimestamp = (value) => {
  if (!value) return 'Not saved this session';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

/** Presentation-only shared view for the regional forecasting workspace. */
const RegionalWorkspaceNotices = ({ vm }) => {
  const { AlertTriangle, CheckCircle2, UploadCloud, XCircle, applyForecastWorkbookImport, canEditSubmission, excelBusy, excelImportPreview, excelNotice, lastSavedAt, returnedComment, retrySave, saveError, saveState, setExcelImportPreview, workflowError } = vm;
  const showSaveError = Boolean(saveError);

  return (
    <>
{returnedComment && (
        <section className="forecast-panel forecast-returned-banner">
          <AlertTriangle size={16} />
          <div>
            <strong>Returned by {returnedComment.actor}</strong>
            <p>{returnedComment.comment}</p>
          </div>
        </section>
      )}

{workflowError && (
        <section className="forecast-panel forecast-returned-banner error">
          <XCircle size={16} />
          <div>
            <strong>Workflow</strong>
            <p>{workflowError}</p>
          </div>
        </section>
      )}

{showSaveError && (
        <section className="forecast-panel forecast-returned-banner error" aria-live="assertive">
          <XCircle size={16} />
          <div>
            <strong>Draft autosave failed</strong>
            <p>{saveError}</p>
            <small>
              Last saved:{' '}
              {lastSavedAt ? (
                <time dateTime={lastSavedAt}>{formatSavedTimestamp(lastSavedAt)}</time>
              ) : formatSavedTimestamp(lastSavedAt)}
            </small>
            <div className="forecast-submit-actions">
              <button
                type="button"
                className="forecast-secondary-button compact"
                disabled={!canEditSubmission || saveState === 'saving'}
                onClick={retrySave}
              >
                {saveState === 'saving' ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </div>
        </section>
      )}

{excelNotice && (
        <section className={`forecast-panel forecast-returned-banner ${excelNotice.tone === 'red' ? 'error' : ''}`}>
          {excelNotice.tone === 'red' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          <div>
            <strong>Excel round trip</strong>
            <p>{excelNotice.message}</p>
          </div>
        </section>
      )}

{excelImportPreview && (
        <section className="forecast-panel forecast-excel-preview">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">XLS_01 · Import preview</span>
              <h2>Review workbook changes before they update this forecast package</h2>
            </div>
            <UploadCloud size={18} />
          </div>
          <blockquote className="forecast-ai-quote">
            <strong>{excelImportPreview.aiSummary?.headline || 'Excel import preview ready'}</strong>
            {(excelImportPreview.aiSummary?.drivers || []).length > 0 && (
              <p>{excelImportPreview.aiSummary.drivers.join(' · ')}</p>
            )}
            {excelImportPreview.aiSummary?.risk && (
              <p className="forecast-ai-risk">Risk: {excelImportPreview.aiSummary.risk}</p>
            )}
            <small>
              {excelImportPreview.aiSummary?.generator === 'haiku'
                ? 'Written by claude-haiku from the workbook diff'
                : 'Rule-generated from the workbook diff'}
            </small>
          </blockquote>
          <div className="forecast-submit-chips">
            <span className={`forecast-health-chip ${excelImportPreview.validation?.status === 'blocked' ? 'red' : 'green'}`}>
              {excelImportPreview.validation?.status === 'blocked' ? 'Blocked' : 'Ready to apply'}
            </span>
            <span className="forecast-health-chip neutral">
              {(excelImportPreview.diffSummary?.changedSections || []).length} changed section(s)
            </span>
            <span className="forecast-health-chip neutral">
              {excelImportPreview.diffSummary?.monthlyMetricOverrideCount || 0} monthly override row(s)
            </span>
            <span className="forecast-health-chip neutral">
              {excelImportPreview.diffSummary?.glRowOverrideCount || 0} GL override row(s)
            </span>
          </div>
          {excelImportPreview.validation?.errors?.length > 0 && (
            <ul className="forecast-submit-note error-list">
              {excelImportPreview.validation.errors.map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
          {excelImportPreview.validation?.warnings?.length > 0 && (
            <ul className="forecast-submit-note">
              {excelImportPreview.validation.warnings.slice(0, 6).map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
          <div className="forecast-submit-actions">
            <button
              type="button"
              className="forecast-primary-button"
              disabled={excelBusy !== null || excelImportPreview.validation?.status === 'blocked' || !canEditSubmission}
              onClick={applyForecastWorkbookImport}
            >
              <UploadCloud size={15} />
              {excelBusy === 'apply' ? 'Applying…' : 'Apply import to draft'}
            </button>
            <button
              type="button"
              className="forecast-secondary-button"
              disabled={excelBusy !== null}
              onClick={() => setExcelImportPreview(null)}
            >
              Cancel import
            </button>
          </div>
        </section>
      )}
    </>
  );
};

export default RegionalWorkspaceNotices;
